/* global XLSX */
const PlummeSettingsDataTools = (() => {
  const text = (value) => String(value ?? '').trim();
  const normalizeHeader = (value) => text(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
  const splitCsvLine = (line, delimiter) => {
    const cells = [];
    let current = '';
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];
      if (character === '"' && quoted && line[index + 1] === '"') { current += '"'; index += 1; continue; }
      if (character === '"') { quoted = !quoted; continue; }
      if (character === delimiter && !quoted) { cells.push(current); current = ''; continue; }
      current += character;
    }
    cells.push(current);
    return cells.map(text);
  };
  const parseCsvMatrix = (value) => {
    const lines = String(value || '').replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
    if (!lines.length) return [];
    const delimiter = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ';' : ',';
    return lines.map((line) => splitCsvLine(line, delimiter));
  };
  const partyFromMatrix = (matrix) => {
    if (!Array.isArray(matrix) || matrix.length < 2) return [];
    const headers = matrix[0].map(normalizeHeader);
    const pick = (row, names) => {
      const index = headers.findIndex((header) => names.includes(header));
      return index >= 0 ? text(row[index]) : '';
    };
    return matrix.slice(1).map((row) => {
      const address = pick(row, ['adresse', 'adresse1', 'address', 'address1', 'gateadresse']);
      const address2 = pick(row, ['adresse2', 'address2']);
      const postalCode = pick(row, ['postnummer', 'postnr', 'postalcode', 'zip']);
      const postalPlace = pick(row, ['poststed', 'postalplace', 'city']);
      return {
        nr: pick(row, ['nr', 'nummer', 'kundenr', 'kundenummer', 'leverandornr', 'leverandrnr', 'leverandornummer', 'leverandrnummer', 'customernumber', 'suppliernumber', 'no']),
        name: pick(row, ['navn', 'name', 'kundenavn', 'leverandornavn', 'customername', 'suppliername']),
        org: pick(row, ['organisasjonsnummer', 'organisasjonsnr', 'orgnr', 'organizationnumber', 'vatnumber']),
        email: pick(row, ['epost', 'email', 'emailadresse']),
        phone: pick(row, ['telefon', 'telefonnummer', 'mobil', 'mobile', 'phone', 'phonenumber']),
        defaultCustomerReference: pick(row, ['fastfakturareferanse', 'fakturareferanse', 'deresreferanse', 'defaultinvoicereference', 'defaultcustomerreference', 'customerreference']),
        addr: [address, address2, postalCode, postalPlace].filter(Boolean).join(', '),
        zip: postalCode,
        city: postalPlace,
        address2,
        status: 'aktiv',
      };
    }).filter((party) => party.name);
  };
  const parsePartyCsv = (value) => partyFromMatrix(parseCsvMatrix(value));
  const parsePartyFile = async (file) => {
    if (!file) return [];
    if (String(file.name || '').toLowerCase().endsWith('.xlsx')) {
      if (!globalThis.XLSX) await loadXLSX();
      const workbook = globalThis.XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName = workbook.SheetNames?.[0];
      if (!sheetName) return [];
      return partyFromMatrix(globalThis.XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, raw: false, defval: '' }));
    }
    return parsePartyCsv(await file.text());
  };
  const parseDecimal = (value, fallback = 0) => {
    const parsed = Number(String(value ?? '').replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const toIsoDate = (value) => {
    const raw = text(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const match = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(raw);
    if (!match) return '';
    const year = match[3].length === 2 ? `20${match[3]}` : match[3];
    return `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  };
  const addDays = (iso, days) => {
    const date = new Date(`${iso}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return iso;
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  };
  const id = (prefix) => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `${prefix}-${Date.now()}-${Math.round(Math.random() * 1e6)}`);
  const truthy = (value) => ['ja', 'yes', 'y', 'betalt', 'paid', '1'].includes(normalizeHeader(value));
  const normalizeVat = (value) => {
    const normalized = normalizeHeader(value);
    if (normalized.includes('fri') || normalized.includes('exempt') || normalized.includes('unntatt')) return { vatRate: 0, vatTreatment: 'exempt' };
    if (normalized === '0' || normalized.includes('0mva') || normalized.includes('zerorated')) return { vatRate: 0, vatTreatment: 'zeroRated' };
    const rate = parseDecimal(normalized, 25);
    return { vatRate: [25, 15, 12, 10, 6, 0].includes(rate) ? rate : 25, vatTreatment: rate ? 'standard' : 'zeroRated' };
  };
  const invoiceHeaderMap = (headers) => {
    const map = new Map();
    headers.map(normalizeHeader).forEach((header, index) => {
      if (['fakturanummer', 'fakturanr', 'faktura', 'invoicenumber'].includes(header)) map.set(index, 'invoiceNumber');
      else if (['kundenr', 'kundenummer', 'customernumber', 'customerid'].includes(header)) map.set(index, 'customerNumber');
      else if (['kundenavn', 'kunde', 'customer', 'customername'].includes(header)) map.set(index, 'customerName');
      else if (['orgnr', 'organisasjonsnummer', 'organizationnumber'].includes(header)) map.set(index, 'organizationNumber');
      else if (['epost', 'email', 'emailadresse'].includes(header)) map.set(index, 'email');
      else if (['telefon', 'tlf', 'phone'].includes(header)) map.set(index, 'phone');
      else if (['adresse', 'address'].includes(header)) map.set(index, 'address');
      else if (['postnr', 'postnummer', 'postalcode'].includes(header)) map.set(index, 'postalCode');
      else if (['poststed', 'postalplace', 'city'].includes(header)) map.set(index, 'postalPlace');
      else if (['dato', 'fakturadato', 'date'].includes(header)) map.set(index, 'date');
      else if (['forfallsdato', 'duedate'].includes(header)) map.set(index, 'dueDate');
      else if (['varreferanse', 'vaarreferanse', 'ourreference', 'ourref'].includes(header)) map.set(index, 'ourReference');
      else if (['deresreferanse', 'kundensreferanse', 'customerreference', 'theirreference', 'theirref'].includes(header)) map.set(index, 'theirReference');
      else if (['referanse', 'reference'].includes(header)) map.set(index, 'reference');
      else if (['kommentar', 'comment'].includes(header)) map.set(index, 'comment');
      else if (['varenr', 'varenummer', 'produktnr', 'produktnummer', 'itemnumber', 'productnumber', 'sku'].includes(header)) map.set(index, 'productNumber');
      else if (['produktnavn', 'produkt', 'productname'].includes(header)) map.set(index, 'productName');
      else if (['beskrivelse', 'description'].includes(header)) map.set(index, 'description');
      else if (['enhet', 'unit', 'unitlabel'].includes(header)) map.set(index, 'unit');
      else if (['antall', 'quantity'].includes(header)) map.set(index, 'quantity');
      else if (['priseksmva', 'priseksmoms', 'enhetspris', 'unitprice'].includes(header)) map.set(index, 'unitPrice');
      else if (['rabatt', 'discount'].includes(header)) map.set(index, 'discountValue');
      else if (['rabattype', 'discounttype'].includes(header)) map.set(index, 'discountType');
      else if (['mva', 'moms', 'vat', 'mvasats'].includes(header)) map.set(index, 'vat');
      else if (['konto', 'account', 'salesaccountcode'].includes(header)) map.set(index, 'salesAccountCode');
      else if (['betalt', 'paid'].includes(header)) map.set(index, 'paid');
    });
    return map;
  };
  const invoiceMatrix = (matrix, existingInvoices = []) => {
    if (!Array.isArray(matrix) || matrix.length < 2) return [];
    const headers = invoiceHeaderMap(matrix[0]);
    const existingNumbers = new Set((existingInvoices || []).map((invoice) => text(invoice.invoiceNumber || invoice.num)).filter(Boolean));
    const importedNumbers = new Set();
    const byNumber = new Map();
    const now = new Date().toISOString();
    matrix.slice(1).forEach((row) => {
      const data = {};
      row.forEach((value, index) => { if (headers.has(index)) data[headers.get(index)] = text(value); });
      const invoiceNumber = text(data.invoiceNumber);
      const customerName = text(data.customerName);
      const date = toIsoDate(data.date);
      if (!invoiceNumber || !customerName || !date) return;
      if (existingNumbers.has(invoiceNumber)) throw new Error('invoice-import-duplicate-existing');
      importedNumbers.add(invoiceNumber);
      const vat = normalizeVat(data.vat);
      const line = {
        id: id('invoice-line'),
        productNumber: text(data.productNumber),
        itemNumber: text(data.productNumber),
        unit: text(data.unit),
        productName: text(data.productName),
        description: text(data.description || data.productName) || 'Importert fakturalinje',
        quantity: parseDecimal(data.quantity, 1) || 1,
        unitPrice: parseDecimal(data.unitPrice, 0),
        discountValue: parseDecimal(data.discountValue, 0),
        discountType: text(data.discountType).toLowerCase() === 'kr' ? 'amount' : 'percent',
        vatRate: vat.vatRate,
        vatTreatment: vat.vatTreatment,
        salesAccountCode: text(data.salesAccountCode) || '3000',
      };
      const current = byNumber.get(invoiceNumber);
      if (current) {
        current.lines.push(line);
        return;
      }
      byNumber.set(invoiceNumber, {
        id: id('invoice-import'),
        invoiceNumber,
        customer: {
          customerNumber: text(data.customerNumber),
          number: text(data.customerNumber),
          name: customerName,
          organizationNumber: text(data.organizationNumber),
          email: text(data.email),
          phone: text(data.phone),
          address: text(data.address),
          postalCode: text(data.postalCode),
          postalPlace: text(data.postalPlace),
        },
        date,
        dueDate: toIsoDate(data.dueDate) || addDays(date, 14),
        reference: text(data.ourReference || data.reference),
        ourReference: text(data.ourReference || data.reference),
        theirReference: text(data.theirReference),
        customerReference: text(data.theirReference),
        comment: text(data.comment),
        lines: [line],
        createdAt: now,
        kind: 'invoice',
        status: 'draft',
        paidAt: truthy(data.paid) ? date : undefined,
        paidBy: truthy(data.paid) ? 'company' : undefined,
      });
    });
    if (importedNumbers.size !== byNumber.size) throw new Error('invoice-import-duplicate-file');
    return [...byNumber.values()];
  };
  const parseInvoiceFile = async (file, existingInvoices = []) => {
    if (!file) return [];
    const lower = String(file.name || '').toLowerCase();
    if (lower.endsWith('.xlsx')) {
      if (!globalThis.XLSX) await loadXLSX();
      const workbook = globalThis.XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName = workbook.SheetNames?.[0];
      if (!sheetName) return [];
      return invoiceMatrix(globalThis.XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, raw: false, defval: '' }), existingInvoices);
    }
    return invoiceMatrix(parseCsvMatrix(await file.text()), existingInvoices);
  };
  const exportInvoiceTemplate = (kind = 'csv') => {
    const columns = ['Fakturanummer', 'Kundenr', 'Kundenavn', 'Orgnr', 'E-post', 'Telefon', 'Adresse', 'Postnr', 'Poststed', 'Dato', 'Forfallsdato', 'V\u00e5r referanse', 'Deres referanse', 'Kommentar', 'Varenr', 'Produktnavn', 'Beskrivelse', 'Enhet', 'Antall', 'Pris eks mva', 'Rabatt', 'Rabattype', 'MVA', 'Konto', 'Betalt'];
    const row = ['1001', '10001', 'Eksempel AS', '999999999', 'post@example.no', '99999999', 'Testveien 1', '0150', 'Oslo', '2026-01-15', '2026-01-29', 'Tore', 'PO-42', '', 'P-100', 'Konsulenttimer', 'Konsulenttimer januar', 'timer', '10', '950', '0', '%', '25', '3000', 'nei'];
    if (kind === 'xlsx') {
      if (!globalThis.XLSX) throw new Error(t('bam.settingsData.excelExportUnavailable'));
      const workbook = globalThis.XLSX.utils.book_new();
      globalThis.XLSX.utils.book_append_sheet(workbook, globalThis.XLSX.utils.aoa_to_sheet([columns, row]), 'Fakturaimport');
      globalThis.XLSX.writeFile(workbook, 'plumme-fakturaimport-mal.xlsx');
      return;
    }
    const csv = [columns.map(escapeCsv).join(';'), row.map(escapeCsv).join(';')].join('\r\n');
    downloadBlob('plumme-fakturaimport-mal.csv', new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  };
  const partyRows = (rows) => (rows || []).map((row) => ({
    number: row.nr || row.customerNumber || row.supplierNumber || '',
    name: row.name || '',
    organizationNumber: row.organizationNumber || row.org || '',
    email: row.email || '',
    phone: row.phone || '',
    defaultCustomerReference: row.defaultCustomerReference || '',
    address: row.address || row.addr || '',
    postalCode: row.postalCode || row.zip || '',
    postalPlace: row.postalPlace || row.city || '',
    address2: row.address2 || '',
    country: row.country || '',
    contactName: row.contactName || row.contactPerson || '',
    status: row.status || 'active',
  }));
  const partyColumns = [
    { header: 'Nummer', value: (row) => row.number },
    { header: 'Navn', value: (row) => row.name },
    { header: 'Organisasjonsnummer', value: (row) => row.organizationNumber },
    { header: 'E-post', value: (row) => row.email },
    { header: 'Telefon', value: (row) => row.phone },
    { header: 'Fast fakturareferanse', value: (row) => row.defaultCustomerReference },
    { header: 'Adresse', value: (row) => row.address },
    { header: 'Postnummer', value: (row) => row.postalCode },
    { header: 'Poststed', value: (row) => row.postalPlace },
    { header: 'Adresselinje 2', value: (row) => row.address2 },
    { header: 'Land', value: (row) => row.country },
    { header: 'Kontaktperson', value: (row) => row.contactName },
    { header: 'Status', value: (row) => row.status },
  ];
  const escapeCsv = (value) => {
    const cell = String(value ?? '');
    return /[";\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  };
  const downloadBlob = (filename, blob) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };
  const exportRows = (filename, sheetName, rows, columns, kind = 'csv') => {
    if (kind === 'xlsx') {
      if (!globalThis.XLSX) throw new Error(t('bam.settingsData.excelExportUnavailable'));
      const matrix = [columns.map((column) => column.header), ...rows.map((row) => columns.map((column) => column.value(row)))];
      const workbook = globalThis.XLSX.utils.book_new();
      const worksheet = globalThis.XLSX.utils.aoa_to_sheet(matrix);
      globalThis.XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
      globalThis.XLSX.writeFile(workbook, `${filename}.xlsx`);
      return;
    }
    const csv = [columns.map((column) => escapeCsv(column.header)).join(';'), ...rows.map((row) => columns.map((column) => escapeCsv(column.value(row))).join(';'))].join('\r\n');
    downloadBlob(`${filename}.csv`, new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  };
  const exportParties = (filename, sheetName, rows, kind) => exportRows(filename, sheetName, partyRows(rows), partyColumns, kind);
  const number = (value) => Number(value) || 0;
  const voucherOverviewRows = (vouchers) => (vouchers || []).map((voucher) => ({
    voucherNumber: voucher.num || '',
    date: voucher.date || '',
    type: voucher.kind || '',
    description: voucher.name || '',
    net: number(voucher.net),
    vat: number(voucher.gross) - number(voucher.net),
    gross: number(voucher.gross),
    status: voucher.status || '',
    sourceType: voucher.sourceType || '',
    sourceId: voucher.sourceId || '',
    paymentStatus: voucher.paymentStatus || '',
    vatReportCodes: (voucher.vatReportCodes || []).join(', '),
    attachments: (voucher.attachments || []).map((attachment) => attachment.name).filter(Boolean).join(', '),
  }));
  const voucherPostingRows = (vouchers, sortByAccount = false) => {
    const rows = (vouchers || []).flatMap((voucher) => (voucher.postings || []).map((posting, lineIndex) => {
      const amount = number(posting.amount);
      return {
        voucherNumber: voucher.num || '',
        date: voucher.date || '',
        accountCode: posting.nr || posting.accountCode || '',
        accountName: posting.name || '',
        description: posting.desc || posting.description || voucher.name || '',
        debit: amount > 0 ? amount : 0,
        credit: amount < 0 ? Math.abs(amount) : 0,
        lineNumber: lineIndex + 1,
        type: voucher.kind || '',
        sourceType: voucher.sourceType || '',
        sourceId: voucher.sourceId || '',
        status: voucher.status || '',
        paymentStatus: voucher.paymentStatus || '',
        vatReportCodes: (voucher.vatReportCodes || []).join(', '),
        attachments: (voucher.attachments || []).map((attachment) => attachment.name).filter(Boolean).join(', '),
      };
    }));
    return sortByAccount ? rows.sort((left, right) => String(left.accountCode).localeCompare(String(right.accountCode)) || String(left.date).localeCompare(String(right.date))) : rows;
  };
  const voucherOverviewColumns = [
    { header: 'Bilagsnr.', value: (row) => row.voucherNumber },
    { header: 'Dato', value: (row) => row.date },
    { header: 'Type', value: (row) => row.type },
    { header: 'Beskrivelse', value: (row) => row.description },
    { header: 'Netto', value: (row) => row.net },
    { header: 'MVA', value: (row) => row.vat },
    { header: 'Brutto', value: (row) => row.gross },
    { header: 'Status', value: (row) => row.status },
    { header: 'Kildetype', value: (row) => row.sourceType },
    { header: 'Kilde-ID', value: (row) => row.sourceId },
    { header: 'Betalingsstatus', value: (row) => row.paymentStatus },
    { header: 'MVA-koder', value: (row) => row.vatReportCodes },
    { header: 'Vedlegg', value: (row) => row.attachments },
  ];
  const voucherPostingColumns = [
    { header: 'Bilagsnr.', value: (row) => row.voucherNumber },
    { header: 'Dato', value: (row) => row.date },
    { header: 'Konto', value: (row) => row.accountCode },
    { header: 'Kontonavn', value: (row) => row.accountName },
    { header: 'Beskrivelse', value: (row) => row.description },
    { header: 'Debet', value: (row) => row.debit },
    { header: 'Kredit', value: (row) => row.credit },
    { header: 'Linje', value: (row) => row.lineNumber },
    { header: 'Type', value: (row) => row.type },
    { header: 'Kildetype', value: (row) => row.sourceType },
    { header: 'Kilde-ID', value: (row) => row.sourceId },
    { header: 'Status', value: (row) => row.status },
    { header: 'Betalingsstatus', value: (row) => row.paymentStatus },
    { header: 'MVA-koder', value: (row) => row.vatReportCodes },
    { header: 'Vedlegg', value: (row) => row.attachments },
  ];
  const buildVoucherExportRows = (vouchers, mode) => mode === 'bilag'
    ? voucherOverviewRows(vouchers)
    : voucherPostingRows(vouchers, mode === 'hovedbok');
  const exportVouchers = (vouchers, mode) => {
    const rows = buildVoucherExportRows(vouchers, mode);
    const columns = mode === 'bilag' ? voucherOverviewColumns : voucherPostingColumns;
    exportRows(`plumme-${mode}`, mode, rows, columns, 'csv');
    return rows.length;
  };
  const reportKey = (value) => text(value).toLocaleLowerCase('nb-NO');
  const openItemAmount = (entry) => number(entry?.remainingAmount || entry?.amountGross);
  const buildAccountingImportOpenItemsReport = (state = {}, filters = {}) => {
    const query = text(filters.query).toLocaleLowerCase('nb-NO');
    const selectedKind = filters.kind || 'all';
    const partyStats = {
      customer: new Map((state.accountingImportPartyStats?.customers || []).map((entry) => [reportKey(entry.partyName), number(entry.outstanding)])),
      supplier: new Map((state.accountingImportPartyStats?.suppliers || []).map((entry) => [reportKey(entry.partyName), number(entry.outstanding)])),
    };
    const allRows = [
      ...(state.accountingImportOpenItems?.customerInvoices || []).map((entry) => ({ ...entry, kind: 'customer', kindLabel: 'Kunde' })),
      ...(state.accountingImportOpenItems?.supplierInvoices || []).map((entry) => ({ ...entry, kind: 'supplier', kindLabel: 'Leverand\u00f8r' })),
    ].filter((entry) => openItemAmount(entry) > 0.005)
      .sort((left, right) => String(left.dueDate || left.date || '').localeCompare(String(right.dueDate || right.date || '')));
    const filteredItems = allRows.filter((entry) => {
      if (selectedKind !== 'all' && entry.kind !== selectedKind) return false;
      if (!query) return true;
      return [entry.kindLabel, entry.partyName, entry.partyNumber, entry.customerNumber, entry.supplierNumber, entry.invoiceNumber, entry.date, entry.dueDate, entry.importMeta?.sheetName]
        .some((value) => text(value).toLocaleLowerCase('nb-NO').includes(query));
    });
    const byParty = filteredItems.reduce((map, entry) => {
      const key = `${entry.kind}:${reportKey(entry.partyName)}`;
      const current = map.get(key) || { kind: entry.kind, label: entry.kindLabel, partyName: entry.partyName || 'Uten navn', total: 0, count: 0 };
      current.total += openItemAmount(entry);
      current.count += 1;
      map.set(key, current);
      return map;
    }, new Map());
    const deviations = Array.from(byParty.values())
      .map((entry) => ({ ...entry, statsOutstanding: partyStats[entry.kind].get(reportKey(entry.partyName)) || 0 }))
      .map((entry) => ({ ...entry, diff: Math.round((entry.total - entry.statsOutstanding) * 100) / 100 }))
      .filter((entry) => Math.abs(entry.diff) > 1);
    const deviationByParty = new Map(deviations.map((entry) => [`${entry.kind}:${reportKey(entry.partyName)}`, entry]));
    const rows = filteredItems.map((entry) => {
      const deviation = deviationByParty.get(`${entry.kind}:${reportKey(entry.partyName)}`);
      return {
        kind: entry.kind,
        type: entry.kindLabel,
        partyName: entry.partyName || 'Uten navn',
        partyNumber: entry.partyNumber || entry.customerNumber || entry.supplierNumber || '',
        invoiceNumber: entry.invoiceNumber || '',
        date: entry.date || '',
        dueDate: entry.dueDate || '',
        amountGross: number(entry.amountGross),
        paidAmount: number(entry.paidAmount),
        remainingAmount: openItemAmount(entry),
        sourceSheet: entry.importMeta?.sheetName || '',
        sourceFile: entry.importMeta?.fileName || '',
        ledgerOutstanding: deviation ? deviation.statsOutstanding : partyStats[entry.kind].get(reportKey(entry.partyName)) || 0,
        partyDeviation: deviation ? deviation.diff : 0,
      };
    });
    return {
      rows,
      deviations,
      total: rows.reduce((sum, row) => sum + number(row.remainingAmount), 0),
      count: rows.length,
    };
  };
  const accountingImportOpenItemColumns = [
    { header: 'Type', value: (row) => row.type },
    { header: 'Navn', value: (row) => row.partyName },
    { header: 'Partnr', value: (row) => row.partyNumber },
    { header: 'Fakturanr', value: (row) => row.invoiceNumber },
    { header: 'Dato', value: (row) => row.date },
    { header: 'Forfall', value: (row) => row.dueDate },
    { header: 'Brutto', value: (row) => row.amountGross },
    { header: 'Betalt', value: (row) => row.paidAmount },
    { header: 'Restbelop', value: (row) => row.remainingAmount },
    { header: 'Reskontrosaldo per part', value: (row) => row.ledgerOutstanding },
    { header: 'Avvik per part', value: (row) => row.partyDeviation },
    { header: 'Kildeark', value: (row) => row.sourceSheet },
    { header: 'Kildefil', value: (row) => row.sourceFile },
  ];
  const accountingImportDeviationColumns = [
    { header: 'Type', value: (row) => row.label },
    { header: 'Navn', value: (row) => row.partyName },
    { header: 'Antall apne poster', value: (row) => row.count },
    { header: 'Sum restbelop', value: (row) => row.total },
    { header: 'Importert reskontrosaldo', value: (row) => row.statsOutstanding },
    { header: 'Avvik', value: (row) => row.diff },
  ];
  const exportAccountingImportOpenItemsReport = (state = {}, filters = {}, kind = 'csv') => {
    const report = buildAccountingImportOpenItemsReport(state, filters);
    const year = Number(state.activeYear) || new Date().getFullYear();
    const suffix = filters.kind && filters.kind !== 'all' ? `-${filters.kind}` : '';
    const filename = `plumme-import-apne-poster-${year}${suffix}`;
    if (kind === 'xlsx') {
      if (!globalThis.XLSX) throw new Error(t('bam.settingsData.excelExportUnavailable'));
      const workbook = globalThis.XLSX.utils.book_new();
      const itemMatrix = [accountingImportOpenItemColumns.map((column) => column.header), ...report.rows.map((row) => accountingImportOpenItemColumns.map((column) => column.value(row)))];
      globalThis.XLSX.utils.book_append_sheet(workbook, globalThis.XLSX.utils.aoa_to_sheet(itemMatrix), 'Apne poster');
      const deviationMatrix = [accountingImportDeviationColumns.map((column) => column.header), ...report.deviations.map((row) => accountingImportDeviationColumns.map((column) => column.value(row)))];
      globalThis.XLSX.utils.book_append_sheet(workbook, globalThis.XLSX.utils.aoa_to_sheet(deviationMatrix), 'Avvik');
      globalThis.XLSX.writeFile(workbook, `${filename}.xlsx`);
      return report;
    }
    exportRows(filename, 'Apne poster', report.rows, accountingImportOpenItemColumns, 'csv');
    return report;
  };
  const rounded = (value) => Math.round(number(value) * 100) / 100;
  const yearFromDate = (value) => {
    const match = /^(\d{4})/.exec(text(value));
    return match ? match[1] : '';
  };
  const balanceLineDebit = (line) => number(line?.debit ?? line?.debet);
  const balanceLineCredit = (line) => number(line?.credit ?? line?.kredit);
  const balanceLineSigned = (line) => rounded(balanceLineDebit(line) - balanceLineCredit(line));
  const bankLedgerCodeFromAccount = (account) => text(account?.ledgerAccountCode || account?.ledger || account?.accountCode || account?.code || account?.accountNumber);
  const isBankLedgerCode = (value) => /^19\d{2}/.test(text(value));
  const importedOpenItemsByYear = (items = []) => items.reduce((map, item) => {
    const year = yearFromDate(item?.date || item?.dueDate) || 'Ukjent';
    const current = map.get(year) || { count: 0, total: 0 };
    current.count += 1;
    current.total += openItemAmount(item);
    map.set(year, current);
    return map;
  }, new Map());
  const sumPartyStatsOutstanding = (items = []) => rounded(items.reduce((sum, item) => sum + number(item?.outstanding), 0));
  const buildAccountingImportTransitionReport = (state = {}) => {
    const openingBalanceByYear = state.openingBalanceByYear || {};
    const reportStatsByYear = state.accountingImportReportStatsByYear || {};
    const ledgerRows = (state.accountingImportLedgerRows || []).filter((row) => row?.archiveOnly === true || row?.importSource === 'complete-accounting-export');
    const customerOpenByYear = importedOpenItemsByYear(state.accountingImportOpenItems?.customerInvoices || []);
    const supplierOpenByYear = importedOpenItemsByYear(state.accountingImportOpenItems?.supplierInvoices || []);
    const ledgerByYear = ledgerRows.reduce((map, row) => {
      const year = text(row.year) || yearFromDate(row.date) || 'Ukjent';
      const current = map.get(year) || { ledgerRows: 0, ledgerDebit: 0, ledgerCredit: 0 };
      current.ledgerRows += 1;
      current.ledgerDebit += number(row.debit);
      current.ledgerCredit += number(row.credit);
      map.set(year, current);
      return map;
    }, new Map());
    const customerLedgerOutstanding = sumPartyStatsOutstanding(state.accountingImportPartyStats?.customers || []);
    const supplierLedgerOutstanding = sumPartyStatsOutstanding(state.accountingImportPartyStats?.suppliers || []);
    const importedBankAccounts = (state.settings?.bankAccounts || []).filter((account) => account?.importedFromChartAccount);
    const years = new Set([
      ...Object.keys(openingBalanceByYear),
      ...Object.keys(reportStatsByYear),
      ...ledgerByYear.keys(),
      ...customerOpenByYear.keys(),
      ...supplierOpenByYear.keys(),
    ]);
    const activeYear = text(state.activeYear) || text(new Date().getFullYear());
    const hasReskontroStats = Math.abs(customerLedgerOutstanding) > 0.005 || Math.abs(supplierLedgerOutstanding) > 0.005;
    if (!years.size && !hasReskontroStats && !importedBankAccounts.length) {
      return { rows: [], totals: { years: 0, openingLines: 0, ledgerRows: 0, openCustomerAmount: 0, openSupplierAmount: 0, warnings: 0 }, latestReskontroYear: '' };
    }
    const latestYear = [...years].filter((year) => year !== 'Ukjent').sort().pop() || activeYear;
    years.add(latestYear);
    const rows = [...years].sort((left, right) => {
      if (left === 'Ukjent') return 1;
      if (right === 'Ukjent') return -1;
      return String(right).localeCompare(String(left), 'nb-NO');
    }).map((year) => {
      const opening = openingBalanceByYear?.[year] || {};
      const importedReportStats = reportStatsByYear?.[year] || {};
      const reportAccounts = Array.isArray(importedReportStats.accounts) ? importedReportStats.accounts : [];
      const balanceLines = Array.isArray(opening.balanceLines) ? opening.balanceLines : [];
      const openingDebit = rounded(balanceLines.reduce((sum, line) => sum + balanceLineDebit(line), 0));
      const openingCredit = rounded(balanceLines.reduce((sum, line) => sum + balanceLineCredit(line), 0));
      const reportOpening = rounded(reportAccounts.reduce((sum, account) => sum + number(account.opening), 0));
      const reportMovement = rounded(reportAccounts.reduce((sum, account) => sum + number(account.movement), 0));
      const reportClosing = rounded(reportAccounts.reduce((sum, account) => sum + number(account.closing), 0));
      const bankOpeningBalance = rounded(balanceLines.filter((line) => isBankLedgerCode(line?.accountCode)).reduce((sum, line) => sum + balanceLineSigned(line), 0));
      const customerOpen = customerOpenByYear.get(year) || { count: 0, total: 0 };
      const supplierOpen = supplierOpenByYear.get(year) || { count: 0, total: 0 };
      const ledger = ledgerByYear.get(year) || { ledgerRows: 0, ledgerDebit: 0, ledgerCredit: 0 };
      const isReskontroYear = String(year) === String(latestYear);
      const customerReskontro = isReskontroYear ? customerLedgerOutstanding : 0;
      const supplierReskontro = isReskontroYear ? supplierLedgerOutstanding : 0;
      const openingDiff = rounded(openingDebit - openingCredit);
      const ledgerDiff = rounded(ledger.ledgerDebit - ledger.ledgerCredit);
      const customerDeviation = isReskontroYear ? rounded(customerOpen.total - customerReskontro) : 0;
      const supplierDeviation = isReskontroYear ? rounded(supplierOpen.total - supplierReskontro) : 0;
      const warningCount = [
        Math.abs(openingDiff) > 1,
        Math.abs(ledgerDiff) > 1,
        isReskontroYear && Math.abs(customerDeviation) > 1,
        isReskontroYear && Math.abs(supplierDeviation) > 1,
      ].filter(Boolean).length;
      return {
        year,
        openingLines: balanceLines.length,
        openingDebit,
        openingCredit,
        openingDiff,
        reportAccountLines: reportAccounts.length,
        reportOpening,
        reportMovement,
        reportClosing,
        reportIncome: rounded(number(importedReportStats.totals?.income)),
        reportCost: rounded(number(importedReportStats.totals?.cost)),
        reportResult: rounded(number(importedReportStats.totals?.result)),
        bankOpeningBalance,
        importedBankAccounts: importedBankAccounts.length,
        openCustomerItems: customerOpen.count,
        openCustomerAmount: rounded(customerOpen.total),
        openSupplierItems: supplierOpen.count,
        openSupplierAmount: rounded(supplierOpen.total),
        customerReskontro,
        supplierReskontro,
        customerDeviation,
        supplierDeviation,
        ledgerRows: ledger.ledgerRows,
        ledgerDebit: rounded(ledger.ledgerDebit),
        ledgerCredit: rounded(ledger.ledgerCredit),
        ledgerDiff,
        reskontroScope: isReskontroYear ? 'Seneste importerte reskontro' : '',
        warningCount,
      };
    });
    return {
      rows,
      totals: {
        years: rows.length,
        openingLines: rows.reduce((sum, row) => sum + row.openingLines, 0),
        reportAccountLines: rows.reduce((sum, row) => sum + row.reportAccountLines, 0),
        ledgerRows: rows.reduce((sum, row) => sum + row.ledgerRows, 0),
        openCustomerAmount: rounded(rows.reduce((sum, row) => sum + row.openCustomerAmount, 0)),
        openSupplierAmount: rounded(rows.reduce((sum, row) => sum + row.openSupplierAmount, 0)),
        warnings: rows.reduce((sum, row) => sum + row.warningCount, 0),
      },
      latestReskontroYear: latestYear,
    };
  };
  const accountingImportTransitionColumns = [
    { header: 'Ar', value: (row) => row.year },
    { header: 'IB linjer', value: (row) => row.openingLines },
    { header: 'IB debet', value: (row) => row.openingDebit },
    { header: 'IB kredit', value: (row) => row.openingCredit },
    { header: 'IB avvik', value: (row) => row.openingDiff },
    { header: 'Historikk kontolinjer', value: (row) => row.reportAccountLines },
    { header: 'Historikk inngaaende', value: (row) => row.reportOpening },
    { header: 'Historikk endring', value: (row) => row.reportMovement },
    { header: 'Historikk utgaaende', value: (row) => row.reportClosing },
    { header: 'Historikk inntekter', value: (row) => row.reportIncome },
    { header: 'Historikk kostnader', value: (row) => row.reportCost },
    { header: 'Historikk resultat', value: (row) => row.reportResult },
    { header: 'Bank IB', value: (row) => row.bankOpeningBalance },
    { header: 'Importerte bankkontoer', value: (row) => row.importedBankAccounts },
    { header: 'Apne kundeposter', value: (row) => row.openCustomerItems },
    { header: 'Kunde restbelop', value: (row) => row.openCustomerAmount },
    { header: 'Kunde reskontro', value: (row) => row.customerReskontro },
    { header: 'Kunde avvik', value: (row) => row.customerDeviation },
    { header: 'Apne leverandorposter', value: (row) => row.openSupplierItems },
    { header: 'Leverandor restbelop', value: (row) => row.openSupplierAmount },
    { header: 'Leverandor reskontro', value: (row) => row.supplierReskontro },
    { header: 'Leverandor avvik', value: (row) => row.supplierDeviation },
    { header: 'Hovedboklinjer', value: (row) => row.ledgerRows },
    { header: 'Hovedbok debet', value: (row) => row.ledgerDebit },
    { header: 'Hovedbok kredit', value: (row) => row.ledgerCredit },
    { header: 'Hovedbok avvik', value: (row) => row.ledgerDiff },
    { header: 'Reskontrogrunnlag', value: (row) => row.reskontroScope },
    { header: 'Varsler', value: (row) => row.warningCount },
  ];
  const exportAccountingImportTransitionReport = (state = {}, kind = 'csv') => {
    const report = buildAccountingImportTransitionReport(state);
    const year = Number(state.activeYear) || new Date().getFullYear();
    exportRows(`plumme-import-overgangsrapport-${year}`, 'Overgang', report.rows, accountingImportTransitionColumns, kind);
    return report;
  };
  const vatPeriodMonthSpan = (period) => {
    if (period === 'monthly') return 1;
    if (period === 'quarterly') return 3;
    if (period === 'yearly') return 12;
    return 2;
  };
  const termLabel = (months) => {
    const labels = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
    if (!months.length) return '';
    if (months.length === 1) return labels[months[0] - 1] || `mnd ${months[0]}`;
    return `${labels[months[0] - 1] || months[0]}-${labels[months[months.length - 1] - 1] || months[months.length - 1]}`;
  };
  const buildAccountingImportVatTaxHistoryReport = (state = {}) => {
    const reportStatsByYear = state.accountingImportReportStatsByYear || {};
    const rows = [];
    const codes = [];
    Object.keys(reportStatsByYear).sort((a, b) => String(b).localeCompare(String(a), 'nb-NO')).forEach((year) => {
      const span = vatPeriodMonthSpan(window.PlummeVatPeriodHistory?.forDate?.(state.settings, `${year}-01-01`) || state.settings?.vatPeriod || 'bimonthly');
      const stats = reportStatsByYear[year] || {};
      const vat = stats.vat || {};
      const monthlyByMonth = new Map((vat.monthly || []).map((row) => [Number(row.month), row]));
      for (let start = 1, term = 1; start <= 12; start += span, term += 1) {
        const months = Array.from({ length: Math.min(span, 13 - start) }, (_, index) => start + index);
        const termRows = months.map((month) => monthlyByMonth.get(month)).filter(Boolean);
        if (!termRows.length) continue;
        const outputVat = rounded(termRows.reduce((sum, row) => sum + number(row.outputVat), 0));
        const inputVat = rounded(termRows.reduce((sum, row) => sum + number(row.inputVat), 0));
        const settlementMovement = rounded(termRows.reduce((sum, row) => sum + number(row.settlementMovement), 0));
        const netVat = rounded(outputVat - inputVat);
        rows.push({
          year,
          term,
          label: termLabel(months),
          months: months.join(', '),
          outputVat,
          inputVat,
          netVat,
          settlementMovement,
          lineCount: termRows.reduce((sum, row) => sum + number(row.lineCount), 0),
          resultBeforeTax: rounded(number(stats.totals?.result)),
          income: rounded(number(stats.totals?.income)),
          cost: rounded(number(stats.totals?.cost)),
          source: vat.source || '',
        });
      }
      (vat.codes || []).forEach((code) => {
        codes.push({
          year,
          month: code.month,
          code: code.code || 'ukjent',
          rate: number(code.rate),
          base: rounded(number(code.base)),
          taxAmount: rounded(number(code.taxAmount)),
          lineCount: number(code.lineCount),
        });
      });
      if (!(vat.monthly || []).length && (stats.totals?.result || stats.totals?.income || stats.totals?.cost)) {
        rows.push({
          year,
          term: '',
          label: 'Hele året',
          months: '',
          outputVat: 0,
          inputVat: 0,
          netVat: 0,
          settlementMovement: 0,
          lineCount: 0,
          resultBeforeTax: rounded(number(stats.totals?.result)),
          income: rounded(number(stats.totals?.income)),
          cost: rounded(number(stats.totals?.cost)),
          source: 'historical-report-stats',
        });
      }
    });
    return {
      rows,
      codes,
      totals: {
        years: new Set(rows.map((row) => row.year)).size,
        terms: rows.filter((row) => row.term !== '').length,
        outputVat: rounded(rows.reduce((sum, row) => sum + number(row.outputVat), 0)),
        inputVat: rounded(rows.reduce((sum, row) => sum + number(row.inputVat), 0)),
        netVat: rounded(rows.reduce((sum, row) => sum + number(row.netVat), 0)),
        resultBeforeTax: rounded(rows.reduce((sum, row, index, allRows) => {
          const firstForYear = allRows.findIndex((candidate) => candidate.year === row.year) === index;
          return firstForYear ? sum + number(row.resultBeforeTax) : sum;
        }, 0)),
      },
    };
  };
  const accountingImportVatTaxHistoryColumns = [
    { header: 'Ar', value: (row) => row.year },
    { header: 'Termin', value: (row) => row.term },
    { header: 'Periode', value: (row) => row.label },
    { header: 'Utgaaende MVA', value: (row) => row.outputVat },
    { header: 'Inngaaende MVA', value: (row) => row.inputVat },
    { header: 'Netto MVA', value: (row) => row.netVat },
    { header: 'Oppgjor 274x', value: (row) => row.settlementMovement },
    { header: 'Resultat for skatt', value: (row) => row.resultBeforeTax },
    { header: 'Inntekter', value: (row) => row.income },
    { header: 'Kostnader', value: (row) => row.cost },
    { header: 'Kilde', value: (row) => row.source },
  ];
  const exportAccountingImportVatTaxHistoryReport = (state = {}, kind = 'csv') => {
    const report = buildAccountingImportVatTaxHistoryReport(state);
    const year = Number(state.activeYear) || new Date().getFullYear();
    exportRows(`plumme-import-mva-skatt-historikk-${year}`, 'MVA skatt', report.rows, accountingImportVatTaxHistoryColumns, kind);
    return report;
  };
  const decodeXml = (value) => String(value || '')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'").replace(/&amp;/g, '&');
  const xmlBlocks = (xml, tag) => [...String(xml || '').matchAll(new RegExp(`<(?:\\w+:)?${tag}\\b[^>]*>([\\s\\S]*?)<\\/(?:\\w+:)?${tag}>`, 'gi'))].map((match) => match[1] || '');
  const xmlText = (xml, tag) => {
    const match = new RegExp(`<(?:\\w+:)?${tag}\\b[^>]*>([\\s\\S]*?)<\\/(?:\\w+:)?${tag}>`, 'i').exec(String(xml || ''));
    return decodeXml(match?.[1] || '').replace(/<[^>]+>/g, '').trim();
  };
  const accountCategory = (code) => {
    const first = String(code || '')[0];
    if (first === '1') return 'asset';
    if (first === '2') return 'liability';
    if (first === '3') return 'income';
    if (['4', '5', '6', '7', '8'].includes(first)) return 'expense';
    return 'other';
  };
  const mergeByKey = (existing, imported, keyFn) => {
    const map = new Map((existing || []).map((entry) => [keyFn(entry), entry]).filter(([key]) => key));
    (imported || []).forEach((entry) => {
      const key = keyFn(entry);
      if (!key) return;
      map.set(key, { ...(map.get(key) || {}), ...entry, id: map.get(key)?.id || entry.id });
    });
    return [...map.values()];
  };
  const parseSaftXmlText = (xml, state = {}) => {
    // Datoen SAF-T-perioden starter. Balanselinjene under leses fra
    // `OpeningBalance`, altsaa saldoen ved starten av perioden — da er det
    // nettopp den datoen Plumme overtar fra, ikke 1. januar.
    const selectionCriteria = xmlBlocks(xml, 'SelectionCriteria')[0] || '';
    const selectionStart = xmlText(selectionCriteria, 'SelectionStartDate') || xmlText(xml, 'SelectionStartDate');
    const genericStart = xmlText(xml, 'StartDate');
    const periodStart = /^\d{4}-\d{2}-\d{2}$/.test(selectionStart)
      ? selectionStart
      : /^\d{4}-\d{2}-\d{2}$/.test(genericStart)
      ? genericStart
      : '';
    const year = Number(periodStart.slice(0, 4)) || Number(state.activeYear) || new Date().getFullYear();
    const now = new Date().toISOString();
    const accountBlocks = xmlBlocks(xml, 'Account');
    const importedAccounts = accountBlocks.map((block) => {
      const code = xmlText(block, 'AccountID');
      if (!code) return null;
      return { id: code, code, name: xmlText(block, 'AccountDescription') || xmlText(block, 'Description') || `Konto ${code}`, category: accountCategory(code), active: true, locked: false };
    }).filter(Boolean);
    const toParty = (block, prefix) => {
      const name = xmlText(block, 'Name') || xmlText(block, 'CompanyName') || xmlText(block, `${prefix}ID`);
      if (!name) return null;
      return {
        id: id(`${prefix.toLowerCase()}-saft`),
        createdAt: now,
        name,
        nr: xmlText(block, `${prefix}ID`),
        ...(prefix === 'Customer' ? { customerNumber: xmlText(block, 'CustomerID') } : {}),
        ...(prefix === 'Supplier' ? { supplierNumber: xmlText(block, 'SupplierID') } : {}),
        organizationNumber: xmlText(block, 'RegistrationNumber') || xmlText(block, 'TaxRegistrationNumber'),
        email: xmlText(block, 'Email'),
        phone: xmlText(block, 'Telephone') || xmlText(block, 'Phone'),
        address: xmlText(block, 'StreetName') || xmlText(block, 'AddressDetail'),
        postalCode: xmlText(block, 'PostalCode'),
        postalPlace: xmlText(block, 'City') || xmlText(block, 'PostalPlace'),
      };
    };
    const amount = (block, tag) => parseDecimal(xmlText(xmlText(block, tag) || '', 'Amount') || xmlText(block, tag), 0);
    const balanceLines = accountBlocks.map((block) => {
      const accountCode = xmlText(block, 'AccountID');
      const debit = amount(block, 'OpeningDebitBalance');
      const credit = amount(block, 'OpeningCreditBalance');
      return accountCode && (debit || credit) ? { id: id('saft-ob'), accountCode, debit, credit, note: 'Importert fra SAF-T' } : null;
    }).filter(Boolean);
    return {
      year,
      chartAccounts: mergeByKey(state.chartAccounts || [], importedAccounts, (entry) => String(entry.code || entry.accountNumber || entry.id || '').trim()),
      customers: mergeByKey(state.customers || [], xmlBlocks(xml, 'Customer').map((block) => toParty(block, 'Customer')).filter(Boolean), (entry) => {
        const org = String(entry.organizationNumber || entry.org || '').replace(/\D/g, '');
        const number = text(entry.customerNumber || entry.nr);
        return org.length === 9 ? `org:${org}` : number ? `number:${number}` : `name:${String(entry.name || '').trim().toLowerCase()}`;
      }),
      suppliers: mergeByKey(state.suppliers || [], xmlBlocks(xml, 'Supplier').map((block) => toParty(block, 'Supplier')).filter(Boolean), (entry) => {
        const org = String(entry.organizationNumber || entry.org || '').replace(/\D/g, '');
        const number = text(entry.supplierNumber || entry.nr);
        return org.length === 9 ? `org:${org}` : number ? `number:${number}` : `name:${String(entry.name || '').trim().toLowerCase()}`;
      }),
      openingBalance: balanceLines.length ? {
        // NB: dette var hardkodet til `${year}-01-01`. Da trodde Plumme at den
        // hadde ansvaret for hele aaret uansett hvilken periode som ble
        // importert, og AI Regnskapsforer krevde bankutskrifter og bilag for
        // maaneder som var fort i det gamle systemet.
        transitionDate: periodStart || `${year}-01-01`,
        salesByAccount: [],
        expensesByAccount: [],
        outgoingVat: 0,
        incomingVat: 0,
        balanceLines,
        note: 'Importert fra SAF-T. Kontroller balansen før rapportering.',
      } : undefined,
    };
  };
  const parseSaftFile = async (file, state = {}) => {
    if (!file) return null;
    if (!String(file.name || '').toLowerCase().endsWith('.xml')) throw new Error(t('bam.settingsData.pickSaftXml'));
    return parseSaftXmlText(await file.text(), state);
  };
  const xmlEscape = (value) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
  const saftPartyXml = (row, kind) => {
    const idValue = row.nr || row.customerNumber || row.supplierNumber || row.id || '';
    if (!idValue && !row.name) return '';
    const tag = kind === 'customer' ? 'Customer' : 'Supplier';
    const idTag = `${tag}ID`;
    const org = String(row.organizationNumber || row.org || '').replace(/\D/g, '');
    return `      <${tag}><Name>${xmlEscape(row.name || idValue)}</Name><Address><StreetName>${xmlEscape(row.address || row.addr || '')}</StreetName><City>${xmlEscape(row.postalPlace || row.city || 'NA')}</City><PostalCode>${xmlEscape(row.postalCode || row.zip || 'NA')}</PostalCode><Country>${xmlEscape(row.country || 'NO')}</Country></Address><${idTag}>${xmlEscape(idValue)}</${idTag}>${org ? `<RegistrationNumber>${xmlEscape(org)}</RegistrationNumber>` : ''}${row.email ? `<Email>${xmlEscape(row.email)}</Email>` : ''}${row.phone ? `<Telephone>${xmlEscape(row.phone)}</Telephone>` : ''}<PartyInfo><Status>${['archived', 'passiv'].includes(row.status) ? 'Inactive' : 'Active'}</Status></PartyInfo></${tag}>`;
  };
  const buildSaftFinancialXml = (state = {}, vouchers = [], options = {}) => {
    if (!globalThis.PlummeSaftExport?.buildSaftFinancialXml) throw new Error('SAF-T export cannot run because the validated export module is not loaded.');
    return globalThis.PlummeSaftExport.buildSaftFinancialXml(state, vouchers, options);
    if ((state.settings?.country || 'NO') !== 'NO') throw new Error(t('bam.settingsData.saftNorwayOnly'));
    const org = String(state.settings?.organizationNumber || state.settings?.orgNumber || '').replace(/\D/g, '');
    if (org.length !== 9) throw new Error(t('bam.settingsData.saftNeedsOrgNumber'));
    const year = Number(state.activeYear) || new Date().getFullYear();
    if (year > 2026) throw new Error(t('bam.settingsData.saft140'));
    const postings = (vouchers || []).flatMap((voucher, voucherIndex) => (voucher.postings || []).map((posting, lineIndex) => ({
      voucher,
      voucherIndex,
      lineIndex,
      account: posting.nr || posting.accountCode || '0000',
      description: posting.desc || posting.description || voucher.name || 'Postering',
      amount: number(posting.amount),
    })));
    const chartNames = new Map((state.chartAccounts || []).map((account) => [String(account.code || account.accountNumber || account.id || ''), account.name || account.description || '']));
    const accounts = [...new Map(postings.map((line) => [String(line.account), line])).entries()]
      .map(([code, line]) => `        <Account><AccountID>${xmlEscape(code)}</AccountID><AccountDescription>${xmlEscape(chartNames.get(code) || line.description || `Konto ${code}`)}</AccountDescription><AccountType>GL</AccountType></Account>`)
      .join('\n');
    const customers = (state.customers || []).map((row) => saftPartyXml(row, 'customer')).filter(Boolean).join('\n');
    const suppliers = (state.suppliers || []).map((row) => saftPartyXml(row, 'supplier')).filter(Boolean).join('\n');
    const customerMaster = customers ? `    <Customers>\n${customers}\n    </Customers>\n` : '';
    const supplierMaster = suppliers ? `    <Suppliers>\n${suppliers}\n    </Suppliers>\n` : '';
    const taxTable = [
      ['0', 'Ingen MVA-behandling', 0, '0'],
      ['1', 'Kjøp, høy sats', 25, '1'],
      ['3', 'Salg, høy sats', 25, '3'],
      ['5', 'Salg utenfor MVA-området', 0, '5'],
    ].map(([code, description, rate, standardCode]) => `      <TaxTableEntry><TaxType>MVA</TaxType><Description>Merverdiavgift</Description><TaxCodeDetails><TaxCode>${code}</TaxCode><EffectiveDate>${year}-01-01</EffectiveDate><Description>${xmlEscape(description)}</Description><TaxPercentage>${rate}</TaxPercentage><Country>NO</Country><StandardTaxCode>${standardCode}</StandardTaxCode><BaseRate>100</BaseRate></TaxCodeDetails></TaxTableEntry>`).join('\n');
    const transactions = (vouchers || []).map((voucher, voucherIndex) => {
      const lines = (voucher.postings || []).map((posting, index) => {
        const amount = number(posting.amount);
        return `              <Line><RecordID>${index + 1}</RecordID><AccountID>${xmlEscape(posting.nr || posting.accountCode || '0000')}</AccountID><Description>${xmlEscape(posting.desc || posting.description || voucher.name || '')}</Description>${amount >= 0 ? `<DebitAmount><Amount>${Math.abs(amount).toFixed(2)}</Amount></DebitAmount>` : `<CreditAmount><Amount>${Math.abs(amount).toFixed(2)}</Amount></CreditAmount>`}</Line>`;
      }).join('\n');
      return `          <Transaction><TransactionID>${xmlEscape(voucher.num || voucher.id || `B-${voucherIndex + 1}`)}</TransactionID><Period>${String(voucher.date || '').slice(3, 5) || '01'}</Period><PeriodYear>${year}</PeriodYear><TransactionDate>${xmlEscape(toIsoDate(voucher.date) || voucher.date || `${year}-01-01`)}</TransactionDate><Description>${xmlEscape(voucher.name || 'Bilag')}</Description><Lines>\n${lines}\n            </Lines></Transaction>`;
    }).join('\n');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<AuditFile xmlns="urn:StandardAuditFile-Taxation-Financial:NO">\n  <Header><AuditFileVersion>1.30</AuditFileVersion><AuditFileCountry>NO</AuditFileCountry><AuditFileDateCreated>${new Date().toISOString().slice(0, 10)}</AuditFileDateCreated><SoftwareCompanyName>Plumme</SoftwareCompanyName><SoftwareID>Plumme</SoftwareID><Company><RegistrationNumber>${xmlEscape(org)}</RegistrationNumber><Name>${xmlEscape(state.settings?.businessName || 'Plumme')}</Name></Company><SelectionCriteria><SelectionStartDate>${year}-01-01</SelectionStartDate><SelectionEndDate>${year}-12-31</SelectionEndDate></SelectionCriteria></Header>\n  <MasterFiles>\n    <GeneralLedgerAccounts>\n${accounts || '        <Account><AccountID>3000</AccountID><AccountDescription>Salgsinntekt</AccountDescription><AccountType>GL</AccountType></Account>'}\n    </GeneralLedgerAccounts>\n${customerMaster}${supplierMaster}    <TaxTable>\n${taxTable}\n    </TaxTable>\n  </MasterFiles>\n  <GeneralLedgerEntries><NumberOfEntries>${vouchers.length}</NumberOfEntries><TotalDebit>${postings.filter((line) => line.amount > 0).reduce((sum, line) => sum + line.amount, 0).toFixed(2)}</TotalDebit><TotalCredit>${Math.abs(postings.filter((line) => line.amount < 0).reduce((sum, line) => sum + line.amount, 0)).toFixed(2)}</TotalCredit><Journal><JournalID>PLUMME</JournalID><Description>Plumme</Description><Transactions>\n${transactions}\n        </Transactions></Journal></GeneralLedgerEntries>\n</AuditFile>`;
    return { xml, year, voucherCount: vouchers.length, postingCount: postings.length };
  };
  const exportSaftFinancialXml = (state = {}, vouchers = [], options = {}) => {
    const result = buildSaftFinancialXml(state, vouchers, options);
    downloadBlob(`plumme-saft-${result.year}.xml`, new Blob([result.xml], { type: 'application/xml;charset=utf-8' }));
    return result;
  };
  return { parsePartyCsv, parsePartyFile, partyRows, parseInvoiceFile, exportInvoiceTemplate, parseSaftXmlText, parseSaftFile, buildSaftFinancialXml, exportSaftFinancialXml, buildVoucherExportRows, exportParties, exportVouchers, buildAccountingImportOpenItemsReport, exportAccountingImportOpenItemsReport, buildAccountingImportTransitionReport, exportAccountingImportTransitionReport, buildAccountingImportVatTaxHistoryReport, exportAccountingImportVatTaxHistoryReport };
})();

Object.assign(globalThis, { PlummeSettingsDataTools });
